/**
 * Bringing a picture into a design in one gesture (DESIGN §9.3).
 *
 * Drop a file on the phone, paste one, or pick *Image* in the palette: all
 * three land here, and all three do the same thing — the picture is on the
 * screen straight away from an object URL, the upload runs behind it, and the
 * block swaps to the real asset when it lands. An empty image block waiting to
 * be filled in is never created, because that is the step a customer should
 * not have to know about.
 *
 * A failed upload leaves nothing behind: `cancelImport` takes the optimistic
 * block back out along with the history entry it pushed (`useDesignEditor`).
 */

import { useCallback, useRef, useState } from "react";

import type { ScreenDesignAsset } from "../api/types";
import { useI18n } from "../i18n";
import type { DropTarget } from "./dnd";
import type { Action } from "./useDesignEditor";
import { IMAGE_TYPES, LIMITS, pendingImage, type Block } from "./spec";

export interface ImageImport {
  /**
   * What this editor has uploaded, merged over the design's own list by the
   * caller. The refetch that follows an upload lands a render or two later,
   * and until it does the block would name an asset nothing can resolve — one
   * frame of "No image" over a picture the customer is looking at.
   */
  uploaded: ScreenDesignAsset[];
  /** How many files are still uploading. */
  pending: number;
  /** A refused file or a failed upload, for the editor's own `ErrorBox`.
   *  The next import clears it, so there is nothing to dismiss. */
  error: unknown;
  /** One block per file, in order, at (or over) `target`. */
  importFiles: (files: readonly File[], target: DropTarget) => void;
}

/** jsdom has no object URLs, and neither has a browser with them switched off:
 *  the block is still inserted, it simply shows the empty-image placeholder. */
function objectUrl(file: File): string {
  return typeof URL?.createObjectURL === "function" ? URL.createObjectURL(file) : "";
}

function revoke(src: string): void {
  if (src && typeof URL?.revokeObjectURL === "function") URL.revokeObjectURL(src);
}

export function useImageImport({
  assets,
  blocks,
  dispatch,
  upload,
  onUploaded,
}: {
  assets: ScreenDesignAsset[];
  blocks: Block[];
  dispatch: (action: Action) => void;
  upload: (file: File) => Promise<ScreenDesignAsset>;
  /** Re-reads the design, so the asset list the picker and the limit count
   *  work from catches up with what was just added. */
  onUploaded: () => Promise<unknown>;
}): ImageImport {
  const { t } = useI18n();
  const [uploaded, setUploaded] = useState<ScreenDesignAsset[]>([]);
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<unknown>(null);

  // The counts are read when the drop happens, not when the handler was made:
  // a second drop while the first is still uploading must see the first.
  const latest = useRef({ assets, blocks, uploaded });
  latest.current = { assets, blocks, uploaded };

  const importFiles = useCallback(
    (files: readonly File[], target: DropTarget) => {
      const { assets: known, blocks: laid, uploaded: mine } = latest.current;
      // The design's own list plus what this editor has added since: the
      // refetch that would merge them may still be in flight.
      const ids = new Set([...known, ...mine].map((asset) => asset.id));
      let assetRoom = LIMITS.assets - ids.size;
      let blockRoom = LIMITS.blocks.max - laid.length;

      const accepted: File[] = [];
      // One line per file, so a customer dropping five things learns which two
      // were refused rather than only the last of them.
      const refused: string[] = [];
      for (const file of files) {
        if (!(IMAGE_TYPES as readonly string[]).includes(file.type)) {
          refused.push(t("design.notAnImage", { name: file.name }));
        } else if (file.size > LIMITS.uploadBytes) {
          refused.push(
            t("design.imageTooLarge", {
              name: file.name,
              mb: Math.round(LIMITS.uploadBytes / (1024 * 1024)),
            }),
          );
        } else if (assetRoom <= 0) {
          // Said rather than attempted: the endpoint answers 409 for this, and
          // a customer should not have to upload a file to find out.
          refused.push(t("design.assetsFull", { max: LIMITS.assets }));
        } else if (target.kind === "insert" && blockRoom <= 0) {
          refused.push(t("design.blocksFull", { max: LIMITS.blocks.max }));
        } else {
          assetRoom -= 1;
          if (target.kind === "insert" || accepted.length > 0) blockRoom -= 1;
          accepted.push(file);
        }
      }
      setError(refused.length > 0 ? refused.join(" ") : null);
      if (accepted.length === 0) return;

      // Every placeholder first, then the uploads: two files dropped together
      // are both on the screen before either round trip finishes.
      const jobs = accepted.map((file, offset) => {
        const src = objectUrl(file);
        // The first file over an existing image replaces it; anything after it
        // is a new block below, so a two-file drop is still two pictures.
        if (target.kind === "replace" && offset === 0) {
          dispatch({ type: "updateBlock", id: target.id, patch: { pendingSrc: src } });
          return { file, src, id: target.id, inserted: false };
        }
        const at =
          target.kind === "insert"
            ? target.index + offset
            : laid.findIndex((block) => block.id === target.id) + offset;
        const block = pendingImage(src);
        dispatch({ type: "insertBlock", block, at });
        return { file, src, id: block.id, inserted: true };
      });

      setPending((count) => count + jobs.length);
      void (async () => {
        for (const job of jobs) {
          try {
            const asset = await upload(job.file);
            // Both in one batch, so no render ever has the block naming an
            // asset whose URL is not known yet.
            setUploaded((list) => [...list, asset]);
            dispatch({ type: "settleImage", id: job.id, asset: asset.id });
            await onUploaded();
          } catch (failure) {
            dispatch({ type: "cancelImport", id: job.id, inserted: job.inserted });
            setError(failure);
          } finally {
            revoke(job.src);
            setPending((count) => count - 1);
          }
        }
      })();
    },
    [dispatch, onUploaded, t, upload],
  );

  return { uploaded, pending, error, importFiles };
}
